import { useAnimation } from 'framer-motion';
import React, { createContext, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { originXYZero, pushTransition } from './transitions';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
export const AnimatedContext = createContext({});
const AnimatedProvider = (props) => {
    // State
    const location = useLocation();
    const firstRender = useRef(true);
    const bodyHeight = useRef(null);
    const store = {
        enter: null,
        exit: null,
    };
    const intermediaryImg = { el: useRef(null), animation: useAnimation() };
    const intermediaryWrapper = useRef(null);
    const playbackHooks = useRef({
        done: [],
        setup: [],
        animate: [],
        appMounted: [],
    });
    // Methods
    const subscribe = (key, cb) => {
        playbackHooks.current[key].push(cb);
    };
    const unsubscribe = (key, cb) => {
        playbackHooks.current[key] = [...playbackHooks.current[key].filter((entry) => entry !== cb)];
    };
    const setIntermediarySrc = async (exitSrc) => {
        const img = intermediaryImg.el.current;
        img.src = exitSrc;
        await img.decode();
    };
    const resetIntermediary = async () => {
        intermediaryWrapper.current.querySelector('.fade-backdrop').classList.remove('transitioning');
        intermediaryImg.el.current.classList.remove('shown');
        intermediaryImg.el.current.classList.remove('transitioning');
        intermediaryImg.el.current.src = '';
    };
    const resetStore = () => {
        store.exit = null;
        store.enter = null;
    };
    const skipAnimation = async (params) => {
        var _a;
        const { animate, done } = playbackHooks.current;
        const { enter, exit, transition, viewport } = params;
        const scrollY = (viewport === null || viewport === void 0 ? void 0 : viewport.scrollY) || 0;
        const header = document.getElementsByTagName('header')[0];
        const catalogContainer = document.getElementById('catalog');
        if ((transition === null || transition === void 0 ? void 0 : transition.direction) === 'out') {
            header === null || header === void 0 ? void 0 : header.classList.add('no-transition');
        }
        await Promise.all(animate.map(async (cb) => await cb(params)));
        enter.animation.set(Object.assign({ opacity: 1, pointerEvents: 'all', y: 0, x: 0, scale: 1 }, originXYZero));
        await Promise.all(done.map(async (cb) => await cb(params)));
        if ((_a = enter === null || enter === void 0 ? void 0 : enter.el) === null || _a === void 0 ? void 0 : _a.current) {
            enter.el.current.style.position = 'static';
        }
        catalogContainer.style.removeProperty('overflow');
        catalogContainer.style.removeProperty('height');
        header === null || header === void 0 ? void 0 : header.classList.remove('transitioning');
        document.documentElement.scrollTop = scrollY;
        exit.done();
        enter.done();
        resetStore();
        if (transition.direction === 'out') {
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
            header === null || header === void 0 ? void 0 : header.classList.remove('no-transition');
        }
    };
    // SECTION - No Transition --------------------------------------------------------------------
    const noTransition = async (params) => {
        const { setup } = playbackHooks.current;
        const { enter, exit, transition } = params;
        const catalogContainer = document.getElementById('catalog');
        if (transition.direction === 'in') {
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
        }
        else {
            catalogContainer.style.height = `${bodyHeight.current}px`;
        }
        const header = document.getElementsByTagName('header')[0];
        if (transition.direction === 'in') {
            header === null || header === void 0 ? void 0 : header.classList.add('header--pdp');
        }
        else {
            header === null || header === void 0 ? void 0 : header.classList.add('no-transition');
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
        }
        enter.el.current.style.zIndex = '0';
        exit.el.current.style.zIndex = '1';
        exit.el.current.style.position = 'relative';
        enter.el.current.style.position = 'absolute';
        // Setup
        await Promise.all(setup.map(async (cb) => await cb(params)));
        skipAnimation(params);
    };
    // !SECTION End No transition
    // SECTION - Push --------------------------------------------------------------------
    const push = async (params) => {
        var _a, _b;
        const { animate, done } = playbackHooks.current;
        const { enter, exit, transition, viewport } = params;
        const catalogContainer = document.getElementById('catalog');
        if (transition.direction === 'in') {
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
        }
        const header = document.getElementsByTagName('header')[0];
        if (window.innerWidth < window.mqObj['md'] && transition.direction === 'in') {
            header === null || header === void 0 ? void 0 : header.classList.add('header--pdp');
            header === null || header === void 0 ? void 0 : header.classList.add('transitioning');
        }
        else if (window.innerWidth > window.mqObj['md']) {
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
        }
        const scale = (transition === null || transition === void 0 ? void 0 : transition.direction) === 'in' ? 0.7 : 1 / 0.7;
        const scrollY = (viewport === null || viewport === void 0 ? void 0 : viewport.scrollY) || 0;
        const exitScrollTop = window.scrollY;
        const winHeight = document.documentElement.clientHeight;
        const exitOriginY = winHeight / 2 + exitScrollTop;
        exit.el.current.style.zIndex = '2'; // Needed for PLP to be over PDP's accordions
        exit.el.current.style.position = 'fixed';
        exit.el.current.style.overflow = `hidden`;
        exit.el.current.style.scale = `1`;
        exit.el.current.style.opacity = `1`;
        const pushTransitionEase = pushTransition.transition.ease.join(',');
        exit.el.current.style.transition = `scale .75s cubic-bezier(${pushTransitionEase}), opacity .33s cubic-bezier(${pushTransitionEase})`;
        enter.el.current.style.transition = `opacity .5s cubic-bezier(${pushTransitionEase})`;
        enter.el.current.style.position = 'static';
        enter.el.current.style.zIndex = '0';
        enter.el.current.style.opacity = '0';
        enter.el.current.style.scale = '1';
        // Setup
        // await Promise.all(setup.map(async (cb: Function) => await cb(params)));
        //check if page is not ready
        if (!((_a = exit === null || exit === void 0 ? void 0 : exit.el) === null || _a === void 0 ? void 0 : _a.current) || !((_b = enter === null || enter === void 0 ? void 0 : enter.el) === null || _b === void 0 ? void 0 : _b.current)) {
            console.warn('ANIMATION SKIPPED FOR AJAX PROBLEM');
            skipAnimation(params);
            return;
        }
        await Promise.all(animate.map(async (cb) => await cb(params)));
        document.documentElement.scrollTop = scrollY;
        exit.el.current.style.top = `-${exitScrollTop}px`;
        exit.el.current.style.transformOrigin = `center ${exitOriginY}px`;
        //  exit.animation.start
        exit.el.current.style.scale = `${1 / scale}`;
        exit.el.current.style.opacity = `0`;
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(1);
            }, 750);
        });
        exit.done();
        enter.el.current.style.opacity = '1';
        // Clean up
        await Promise.all(done.map(async (cb) => await cb(params)));
        if (transition.direction === 'in' && window.isMobile()) {
            enter.el.current.querySelector('.product-hero-gallery').classList.add('fix-flicker-ios');
        }
        enter.el.current.style.pointerEvents = 'all';
        enter.el.current.style.scale = '';
        enter.done();
        resetStore();
        if (transition.direction === 'out') {
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
        }
        header === null || header === void 0 ? void 0 : header.classList.remove('transitioning');
        if (transition.direction === 'in' && window.isMobile()) {
            setTimeout(() => {
                enter.el.current.querySelector('.product-hero-gallery').classList.remove('fix-flicker-ios');
            }, 150);
        }
    };
    // !SECTION End Push
    const getCustomTime = () => {
        const cookiename = 'devtranstime';
        let name = cookiename + '=';
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return parseInt(c.substring(name.length, c.length));
            }
        }
        return null;
    };
    const CUSTOM_TIME = getCustomTime();
    const TOTAL_TIME = CUSTOM_TIME || 1.2;
    const handleTime = (time) => {
        return (time / 12) * TOTAL_TIME;
    };
    // !SECTION End Zoomout
    // SECTION Zoom in --------------------------------------------------------------------
    const handleZoomIn = async (params) => {
        var _a, _b;
        const { setup, animate, done } = playbackHooks.current;
        const { enter, exit, transition } = params;
        const { state } = location;
        const header = document.getElementsByTagName('header')[0];
        const tickerBar = document.querySelector('.ticker-bar');
        const catalogContainer = document.getElementById('catalog');
        // const stickyTray = document.querySelector('.sticky-tray') as HTMLElement;
        const exitTile = document.querySelector(`[data-productuuid="${state.transition.productUuid}"]`);
        if (transition.direction === 'in') {
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
        }
        else {
            catalogContainer.style.height = `${bodyHeight.current}px`;
        }
        header.classList.add('header--pdp');
        header.classList.add('transitioning');
        // Setup
        enter.el.current.style.position = 'absolute';
        catalogContainer.style.overflow = 'hidden';
        // if (stickyTray) stickyTray.style.opacity = '0';
        await Promise.all(setup.map(async (cb) => await cb(params)));
        const availableEnterAnimatedImages = enter.animatedImages.filter((image) => image.productId);
        const enterImagesCount = availableEnterAnimatedImages.length;
        let enterImage;
        if (enterImagesCount === 1) {
            enterImage = availableEnterAnimatedImages[0];
        }
        else {
            enterImage = availableEnterAnimatedImages.find((i) => {
                return i.productId === state.transition.productId;
            });
        }
        const exitImage = exit.animatedImages.find((image) => {
            return image.productUuid === state.transition.productUuid;
        });
        //check if page is not ready
        if (!((_a = enterImage === null || enterImage === void 0 ? void 0 : enterImage.el) === null || _a === void 0 ? void 0 : _a.current) || !((_b = exitImage === null || exitImage === void 0 ? void 0 : exitImage.el) === null || _b === void 0 ? void 0 : _b.current)) {
            console.warn('ANIMATION SKIPPED FOR AJAX PROBLEM');
            skipAnimation(params);
            await resetIntermediary();
            return;
        }
        const exitingImageSrc = enterImage.el.current.dataset.qualityImg;
        try {
            await setIntermediarySrc(exitingImageSrc);
        }
        catch (error) {
            console.log('intermediaryImage Decoding error', error);
            skipAnimation(params);
            await resetIntermediary();
            return;
        }
        // ANCHOR zoomIN standard
        const exitRect = exitImage.el.current.getBoundingClientRect(); // Repaint
        const exitTileRect = exitTile.getBoundingClientRect(); // Repaint
        const enterRect = enterImage.el.current.getBoundingClientRect(); // Repaint
        const scale = exitRect.width / enterRect.width;
        const leftAnchored = exitTileRect.left <= 0;
        intermediaryImg.el.current.style.setProperty('transform-origin', leftAnchored ? '0 0' : '100% 0');
        let intermediaryX = 0;
        let intermediaryY = exitTileRect.top / scale;
        intermediaryImg.el.current.style.setProperty('--intermediaryT', `scale(${scale}) translate(${intermediaryX}px, ${intermediaryY}px)`);
        intermediaryImg.el.current.classList.add('shown');
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(1);
            }, 100);
        });
        // animazioni di setup
        enter.animation.set({
            pointerEvents: 'all',
            y: 0,
            zIndex: 10,
        });
        await Promise.all(animate.map(async (cb) => await cb(params)));
        // Actual Transitions
        // ANCHOR * INTERMEDIARY 🌆 --------------------------------------------------------------------
        intermediaryY = tickerBar ? tickerBar.offsetHeight : 0;
        intermediaryImg.el.current.classList.add('transitioning');
        intermediaryWrapper.current.querySelector('.fade-backdrop').classList.add('transitioning');
        intermediaryImg.el.current.style.setProperty('--intermediaryT', `translate(-${intermediaryX}px, ${intermediaryY}px)`);
        const finalScale = (enterRect.height / exitRect.height) * scale;
        intermediaryX = ((enterRect.width * finalScale - window.innerWidth) / 2 / finalScale) * (leftAnchored ? -1 : 1);
        // Animating both scale and translate creates an unwanted "curve" in the tile's movement, better add scale in a second moment
        intermediaryImg.el.current.style.setProperty('--intermediaryT', `scale(${finalScale}) translate(${intermediaryX}px, ${intermediaryY}px)`);
        await new Promise((resolve) => {
            intermediaryImg.el.current.addEventListener('transitionend', () => {
                resolve(1);
            }, { once: true });
        });
        // ANCHOR * EXIT ❌ --------------------------------------------------------------------
        // Removed EXIT animation ( the exit will be covered - not needed )
        // @ts-ignore scrollBehavior should accepts 'instant' indeed
        window.scroll({ top: 0, left: 0, behavior: 'instant' });
        // ANCHOR * ENTER ✅ --------------------------------------------------------------------
        await enter.animation.start({
            opacity: 1,
            transition: {
                duration: handleTime(2),
            },
        });
        // ANCHOR * CLEAN UP ⭐️ -------------------------------------------------------------------
        resetIntermediary();
        enter.el.current.style.position = 'static';
        enter.el.current.style.scale = '';
        exit.el.current.style.position = 'static';
        header.classList.remove('transitioning');
        catalogContainer.style.removeProperty('overflow');
        catalogContainer.style.removeProperty('height');
        exit.done();
        enter.done();
        await Promise.all(done.map(async (cb) => await cb(params)));
        resetStore();
    };
    // !SECTION
    const submitEnter = async (enter) => {
        if (firstRender.current) {
            const { appMounted } = playbackHooks.current;
            await Promise.all(appMounted.map(async (cb) => {
                await cb();
            }));
            firstRender.current = false;
        }
        else {
            store.enter = enter;
            ready();
        }
    };
    const submitExit = (exit) => {
        store.exit = exit;
        ready();
    };
    // ANCHOR - Ready --------------------------------------------------------------------
    const ready = async () => {
        const { state } = location;
        const { exit, enter } = store;
        if (!exit || !enter)
            return;
        // OE-14793 - PLP click on color swatch not works the zoom()
        const animationObject = {
            exit,
            enter,
            viewport: state === null || state === void 0 ? void 0 : state.viewport,
            transition: state === null || state === void 0 ? void 0 : state.transition,
        };
        if (window.tileToPlpTransition !== 'none') {
            if (window.tileToPlpTransition === 'push') {
                await push(animationObject);
            }
            else if (window.tileToPlpTransition === 'scale') {
                if (state.transition.direction === 'in' && window.innerWidth < window.mqObj['md']) {
                    await handleZoomIn(animationObject);
                }
                else {
                    await push(animationObject);
                }
            }
        }
        else {
            await noTransition(animationObject);
        }
        if (state.transition.direction === 'out') {
            ScrollTrigger.disable();
        }
        else {
            ScrollTrigger.refresh();
        }
        // update global variable in order to force chat refresh
        //@ts-ignore
        if (window.chatObject)
            window.chatObject.value = new Date().getTime();
    };
    const context = {
        submitExit,
        submitEnter,
        subscribe,
        unsubscribe,
    };
    // Render
    return (React.createElement(AnimatedContext.Provider, { value: context },
        React.createElement("div", { ref: intermediaryWrapper, className: "intermediary-wrapper" },
            React.createElement("img", { ref: intermediaryImg.el, src: "", alt: "", className: "intermediary-image" }),
            React.createElement("div", { className: "fade-backdrop" })),
        props.children));
};
export const useAnimatedContext = () => {
    return useContext(AnimatedContext);
};
export default AnimatedProvider;
